import type {Demand, Offer, OfferRequest, Tender, ViewableTender} from './types';
import type {TenderPhase} from './types';
import type {TenderService} from './tender.service';
import type {OfferingState} from './offering-state';
import {BehaviorSubject} from 'rxjs';

export class TenderPresenter {
    public static presenterName = 'tenderPresenter';

    /*@ngInject*/
    constructor(private tenderService: TenderService) {
    }

    public async getTenderById(id: number): Promise<Tender> {
        return this.tenderService.getTenderById(id);
    }

    public async getDemands(tender: ViewableTender): Promise<Demand[]> {
        return this.tenderService.getDemandsByTender(tender);
    }

    public async confirmAgreement(tender: ViewableTender): Promise<void> {
        await this.tenderService.confirmAgreement(tender);
    }

    public async reloadOfferingStates(tender: ViewableTender): Promise<void> {
        await this.tenderService.reloadOfferingStates(tender);
    }

    public async newOfferingStates(tender: ViewableTender): Promise<OfferingState[]> {
        return this.tenderService.newOfferingStates(tender);
    }

    public async saveOffer(offer: OfferRequest): Promise<Offer> {
        try {
            return await this.tenderService.saveOffer(offer);
        } catch (exceptionResponse) {
            throw exceptionResponse;
        }
    }

    public async removeOffer(offer: Offer, tender: ViewableTender): Promise<void> {
        await this.tenderService.removeOffer(offer, tender);
    }

    public async onAgreementFileSelect(files: FileList, tender: ViewableTender): Promise<void> {
        await this.tenderService.onAgreementFileSelect(files, tender);
    }

    public async createViewableTender(tender: Tender): Promise<ViewableTender> {
        const viewableTender = {
            ...tender,
        } as ViewableTender;


        let phaseSubject: BehaviorSubject<TenderPhase>;

        const currentTime = new Date().getTime();
        const endTime = new Date(tender.endDate).getTime();
        const startTime = new Date(tender.startDate).getTime();
        const openTime = new Date(tender.openDate).getTime();

        if (currentTime > endTime) {
            phaseSubject = new BehaviorSubject('FINISHED');
        } else if (currentTime > startTime) {
            phaseSubject = new BehaviorSubject('STARTED');
        } else if (currentTime > openTime) {
            phaseSubject = new BehaviorSubject('OPENED');
        } else {
            phaseSubject = new BehaviorSubject('CREATED');
        }

        function setChangePhaseAfterDelay(tenderToChange, delay, finalPhase) {
            if (delay > 0 && delay <= 0x7FFFFFFF) {
                setTimeout(function () {
                    phaseSubject.next(finalPhase)
                }, delay);
            }
        }

        const delayToOpen = openTime - currentTime;
        setChangePhaseAfterDelay(tender, delayToOpen, 'OPENED');

        const delayToStart = startTime - currentTime;
        setChangePhaseAfterDelay(tender, delayToStart, 'STARTED');

        const delayToFinish = endTime - currentTime;
        setChangePhaseAfterDelay(tender, delayToFinish, 'FINISHED');

        viewableTender.phase$ = phaseSubject.asObservable();
        viewableTender.offers$ = new BehaviorSubject(await this.newOfferingStates(viewableTender));

        return viewableTender;
    }
}